import React, { useEffect, useState, useCallback } from "react";
import { Star, Trash2, Plus, Loader2, Eye, EyeOff, Save, Quote } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", location: "", text_en: "", text_bn: "", text_hi: "",
  rating: 5, service_title: "", published: false, order: 0,
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.16em] text-tara-muted mb-1.5">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-tara-gold/25 bg-white/85 px-4 py-2.5 text-sm text-tara-ink placeholder:text-tara-muted/70 outline-none focus:border-tara-gold/60";

export default function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/testimonials");
      setItems(r.data || []);
    } catch { toast.error("Could not load testimonials"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text_en.trim()) {
      toast.error("Name and the English testimonial are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/admin/testimonials/${editing}`, form);
        toast.success("Testimonial updated");
      } else {
        await api.post("/admin/testimonials", form);
        toast.success("Testimonial added");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const togglePublish = async (item) => {
    try {
      await api.patch(`/admin/testimonials/${item.id}`, { published: !item.published });
      toast.success(item.published ? "Unpublished" : "Published to homepage");
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const edit = (item) => {
    setEditing(item.id);
    setForm({ ...EMPTY, ...item });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8" data-testid="testimonials-panel">
      {/* Form */}
      <form onSubmit={submit} className="glass-strong rounded-2xl p-6 space-y-4 h-fit lg:sticky lg:top-6" data-testid="testimonial-form">
        <h3 className="font-serif text-lg font-bold text-tara-ink flex items-center gap-2">
          <Quote className="h-4 w-4 text-tara-gold" />
          {editing ? "Edit testimonial" : "Add a devotee testimonial"}
        </h3>

        <Field label="Devotee name *">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Ananya Sen" data-testid="testimonial-name-input" />
        </Field>
        <Field label="City / place">
          <input value={form.location} onChange={(e) => set("location", e.target.value)} className={inputCls} placeholder="Kolkata" data-testid="testimonial-location-input" />
        </Field>
        <Field label="Service (optional)">
          <input value={form.service_title} onChange={(e) => set("service_title", e.target.value)} className={inputCls} placeholder="Maa Tara Special Puja" data-testid="testimonial-service-input" />
        </Field>
        <Field label="Testimonial — English *">
          <textarea value={form.text_en} onChange={(e) => set("text_en", e.target.value)} rows={4} className={inputCls} data-testid="testimonial-text-en-input" />
        </Field>
        <Field label="Testimonial — বাংলা (optional)">
          <textarea value={form.text_bn} onChange={(e) => set("text_bn", e.target.value)} rows={3} className={inputCls} data-testid="testimonial-text-bn-input" />
        </Field>
        <Field label="Testimonial — हिन्दी (optional)">
          <textarea value={form.text_hi} onChange={(e) => set("text_hi", e.target.value)} rows={3} className={inputCls} data-testid="testimonial-text-hi-input" />
        </Field>

        <div className="flex items-center gap-4">
          <Field label="Rating">
            <select value={form.rating} onChange={(e) => set("rating", Number(e.target.value))} className={inputCls} data-testid="testimonial-rating-select">
              {[5, 4, 3].map((r) => <option key={r} value={r}>{`${r} stars`}</option>)}
            </select>
          </Field>
          <Field label="Order">
            <input type="number" value={form.order} onChange={(e) => set("order", Number(e.target.value))} className={inputCls} data-testid="testimonial-order-input" />
          </Field>
        </div>

        <label className="flex items-center gap-3 text-sm text-tara-body cursor-pointer">
          <input type="checkbox" checked={!!form.published} onChange={(e) => set("published", e.target.checked)} className="h-4 w-4 accent-[#B4232A]" data-testid="testimonial-published-checkbox" />
          Publish on the homepage
        </label>

        <div className="flex gap-3 pt-1">
          <button type="submit" disabled={saving} className="btn-sacred flex items-center gap-2 rounded-full px-6 py-3 text-sm font-serif font-semibold" data-testid="testimonial-submit-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {editing ? "Save changes" : "Add testimonial"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} className="btn-ghost-gold rounded-full px-5 py-3 text-sm font-semibold" data-testid="testimonial-cancel-btn">
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* List */}
      <div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-tara-gold animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center text-tara-muted" data-testid="testimonials-empty">
            No testimonials yet. Add the first real devotee voice — only published ones appear on the homepage.
          </div>
        ) : (
          <div className="space-y-4" data-testid="testimonial-list">
            {items.map((v) => (
              <div key={v.id} className="glass rounded-2xl p-5" data-testid={`testimonial-${v.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-display text-xl font-semibold text-tara-ink">{v.name}</h4>
                      {v.location && <span className="text-xs text-tara-muted">· {v.location}</span>}
                      <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                        v.published ? "text-green-800 bg-green-100 border-green-300" : "text-stone-700 bg-stone-100 border-stone-300"
                      }`}>
                        {v.published ? "published" : "draft"}
                      </span>
                    </div>
                    <span className="flex items-center gap-0.5 text-tara-gold mt-1.5">
                      {Array.from({ length: v.rating || 5 }).map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => togglePublish(v)} className="rounded-full border border-tara-gold/30 p-2 text-tara-goldDeep hover:bg-tara-gold/10 transition" data-testid={`testimonial-toggle-${v.id}`} aria-label="Toggle publish">
                      {v.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => edit(v)} className="rounded-full border border-tara-gold/30 px-3 py-2 text-xs font-semibold text-tara-goldDeep hover:bg-tara-gold/10 transition" data-testid={`testimonial-edit-${v.id}`}>
                      Edit
                    </button>
                    <button onClick={() => remove(v.id)} className="rounded-full border border-red-500/40 p-2 text-red-600 hover:bg-red-500/10 transition" data-testid={`testimonial-delete-${v.id}`} aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-tara-body italic leading-relaxed">“{v.text_en}”</p>
                {v.text_bn && <p className="text-sm text-tara-muted italic mt-2">“{v.text_bn}”</p>}
                {v.text_hi && <p className="text-sm text-tara-muted italic mt-2">“{v.text_hi}”</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
